/**
 * Lap Times → Sectors (races whose timing gives sector times: SRO, GR Cup, IMSA): every car in view
 * with its best and median sector over its green racing laps, its theoretical best (best three
 * sectors added up), the spread of each sector and its consistency (the qualifying stats table,
 * StatsTable, in its race form). The focus car's row is marked. On a laptop a switch above Lap Times
 * picks the rank chart or this; on a phone it is the third Times tab (PhoneLapTimes).
 */
import { useMemo, useState, type ReactNode } from "react";
import type { RaceChartData } from "@shared/types";
import { StatsTable } from "../qualifying/StatsTable";
import { TraceControls } from "./TraceControls";
import type { TraceView } from "./trace-view";
import { carNo } from "./car-number";
import { hasSectors, sectorStatsData } from "./sectors";
import { CHART_STYLE, TRACE } from "./constants";

export function SectorsView({
  data,
  focusNum,
  setFocusNum,
  compSet,
  classView,
  view,
  headSlot = null,
  phoneSlot = null,
  controls = true,
}: {
  data: RaceChartData;
  focusNum: number;
  setFocusNum: (n: number) => void;
  compSet: Set<number>;
  classView: string;
  view: TraceView;
  headSlot?: HTMLElement | null;
  phoneSlot?: HTMLElement | null;
  /** False when the page shows the car and class controls itself (a phone's Times tab). */
  controls?: boolean;
}) {
  const stats = useMemo(() => sectorStatsData(data, "Race"), [data]);
  // The field the charts draw (class view and makes); the table filters by class itself.
  const inView = useMemo(
    () => new Set(view.fieldCars.map((n) => carNo(data, n))),
    [view.fieldCars, data]
  );
  return (
    <div
      className="flex flex-col md:h-full md:min-h-0"
      style={{ background: CHART_STYLE.bg, color: CHART_STYLE.text }}
    >
      {controls && (
        <TraceControls
          data={data}
          focusNum={focusNum}
          onFocus={setFocusNum}
          classView={classView}
          compSet={compSet}
          view={view}
          headSlot={headSlot}
          phoneSlot={phoneSlot}
        />
      )}
      <div className="custom-scroll flex flex-col gap-2 px-3 py-3 sm:px-6 md:min-h-0 md:flex-1 md:overflow-y-auto">
        <StatsTable
          data={stats}
          compSet={inView}
          classView={classView}
          kind="race"
          focus={carNo(data, focusNum)}
        />
        <p
          className="m-0 text-[12px] leading-relaxed"
          style={{ color: CHART_STYLE.muted }}
        >
          Green racing laps only: no caution laps, pit laps or out-laps, and none slower
          than the race&apos;s green-pace cutoff. Med: the median, half its laps were
          quicker. σ: the spread of its times. Theo. best: its best three sectors added
          up.
        </p>
      </div>
    </div>
  );
}

const MODE_KEY = "rt.lapTimesView";
type Mode = "rank" | "sectors";
function readMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === "sectors" ? "sectors" : "rank";
  } catch {
    return "rank";
  }
}

/** A laptop's Lap Times: the rank chart, or Sectors when the race's timing has them. */
export function LapTimesSwitch({
  data,
  chart,
  sectors,
}: {
  data: RaceChartData;
  chart: ReactNode;
  sectors: ReactNode;
}) {
  const [mode, setMode] = useState<Mode>(readMode);
  if (!hasSectors(data)) return <>{chart}</>;
  const pick = (m: Mode) => {
    setMode(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      /* private window: for this visit only */
    }
  };
  return (
    <div className="flex flex-col md:min-h-0 md:flex-1">
      <div
        role="tablist"
        aria-label="Lap Times view"
        className="flex shrink-0 gap-1 px-2 pt-2 sm:px-6"
      >
        {(["rank", "sectors"] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => pick(m)}
            title={
              m === "rank"
                ? "Every car's lap-time rank, lap by lap"
                : "Best and median sectors, theoretical best and spread, over green racing laps"
            }
            className="min-h-[36px] rounded-md border px-3 text-[13px] font-semibold"
            style={
              mode === m
                ? { background: TRACE.focus, borderColor: TRACE.focus, color: "#0b0b18" }
                : { borderColor: CHART_STYLE.border, color: CHART_STYLE.muted }
            }
          >
            {m === "rank" ? "Lap time rank" : "Sectors"}
          </button>
        ))}
      </div>
      <div className="flex flex-col md:min-h-0 md:flex-1">
        {mode === "rank" ? chart : sectors}
      </div>
    </div>
  );
}
